import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DuplicateWarningModal = ({ isOpen, duplicateEntry, onOverride, onCancel }) => {
  if (!isOpen || !duplicateEntry) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  return (
    <div className="fixed inset-0 z-300 flex items-center justify-center p-16 md:p-24">
      <div 
        className="absolute inset-0 bg-foreground/60 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-card rounded-lg shadow-elevation-5 border border-border w-full max-w-[480px] md:max-w-[560px] max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-error/10 border-b border-error/20 px-24 md:px-32 py-24">
          <div className="flex items-start gap-18">
            <div className="w-[48px] h-[48px] bg-error/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="AlertTriangle" size={24} color="var(--color-error)" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg md:text-xl text-error mb-6">
                Duplicate Tracking Number Detected
              </h3>
              <p className="font-body text-sm md:text-base text-error/90">
                This tracking number has already been processed
              </p>
            </div>
            <button
              onClick={onCancel}
              className="w-[36px] h-[36px] flex items-center justify-center rounded-lg hover:bg-error/20 transition-smooth flex-shrink-0"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} color="var(--color-error)" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="px-24 md:px-32 py-32 space-y-32">
          <div className="bg-muted rounded-lg p-24 space-y-18">
            <div className="flex items-center justify-between pb-18 border-b border-border">
              <span className="font-caption text-xs text-muted-foreground uppercase tracking-wide">
                Tracking Number
              </span>
              <span className="font-mono font-semibold text-base md:text-lg text-foreground">
                {duplicateEntry?.trackingNumber}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-18">
              <div>
                <span className="font-caption text-xs text-muted-foreground uppercase tracking-wide block mb-6">
                  Original Ship Date
                </span>
                <div className="flex items-center gap-8">
                  <Icon name="Calendar" size={16} color="var(--color-foreground)" strokeWidth={2} />
                  <span className="font-body text-sm md:text-base text-foreground font-medium">
                    {formatDate(duplicateEntry?.timestamp)}
                  </span>
                </div>
                <span className="font-caption text-xs text-muted-foreground mt-4 block">
                  {getDaysAgo(duplicateEntry?.timestamp)}
                </span>
              </div>

              <div>
                <span className="font-caption text-xs text-muted-foreground uppercase tracking-wide block mb-6">
                  Processed By
                </span>
                <div className="flex items-center gap-8">
                  <Icon name="User" size={16} color="var(--color-foreground)" strokeWidth={2} />
                  <span className="font-body text-sm md:text-base text-foreground font-medium">
                    {duplicateEntry?.user}
                  </span>
                </div>
              </div>

              <div>
                <span className="font-caption text-xs text-muted-foreground uppercase tracking-wide block mb-6">
                  Carrier
                </span>
                <div className="flex items-center gap-8">
                  <Icon name="Truck" size={16} color="var(--color-foreground)" strokeWidth={2} />
                  <span className="font-body text-sm md:text-base text-foreground font-medium">
                    {duplicateEntry?.carrier}
                  </span>
                </div>
              </div>

              <div>
                <span className="font-caption text-xs text-muted-foreground uppercase tracking-wide block mb-6">
                  Entry ID
                </span>
                <div className="flex items-center gap-8">
                  <Icon name="Hash" size={16} color="var(--color-foreground)" strokeWidth={2} />
                  <span className="font-mono text-xs md:text-sm text-foreground">
                    {duplicateEntry?.entryId}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-warning/10 border border-warning/20 rounded-lg p-18">
            <div className="flex gap-12">
              <Icon name="Info" size={18} color="var(--color-warning-foreground)" strokeWidth={2} className="flex-shrink-0 mt-2" />
              <div className="space-y-8">
                <p className="font-body text-sm md:text-base text-warning-foreground font-medium">
                  Override Warning
                </p>
                <p className="font-body text-xs md:text-sm text-warning-foreground/90 leading-relaxed">
                  Overriding this duplicate will create a new entry with the same tracking number. This action will be logged in the audit trail and may require manager approval.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-12">
            <Button
              variant="outline"
              size="lg"
              iconName="X"
              onClick={onCancel}
              fullWidth
            >
              Cancel Entry
            </Button>
            <Button
              variant="destructive"
              size="lg"
              iconName="AlertTriangle"
              onClick={onOverride}
              fullWidth
            >
              Override & Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarningModal;